import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { setGapCursorSelection, Side } from '@atlaskit/editor-common/selection';
import { CodeBlockSharedCssClassName, getOrderedListInlineStyles, listItemCounterPadding } from '@atlaskit/editor-common/styles';
import { getItemCounterDigitsSize, isListNode, pluginFactory } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { findParentNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { insm } from '@atlaskit/insm';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { isWrappingPossible } from './utils/selection';
var listPluginKey = new PluginKey('listPlugin');
export var pluginKey = listPluginKey;
var initialState = {
  bulletListActive: false,
  bulletListDisabled: false,
  orderedListActive: false,
  orderedListDisabled: false,
  decorationSet: DecorationSet.empty
};
export var computeListDecorations = function computeListDecorations(doc) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var to = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : doc.content.size;
  var decorations = [];

  // this stack keeps track of each (nested) list to calculate the indentation level
  var processedListsStack = [];

  // @ts-ignore - Workaround for help-center local consumption

  doc.nodesBetween(from, to, function (node, currentNodeStartPos) {
    if (processedListsStack.length > 0) {
      var isOutsideLastList = true;
      while (isOutsideLastList && processedListsStack.length > 0) {
        var lastList = processedListsStack[processedListsStack.length - 1];
        var lastListEndPos = lastList.startPos + lastList.node.nodeSize;
        isOutsideLastList = currentNodeStartPos >= lastListEndPos;
        // once we finish iterating over each innermost list, pop the stack to
        // decrease the indent level attribute accordingly
        if (isOutsideLastList) {
          processedListsStack.pop();
        }
      }
    }
    if (isListNode(node)) {
      processedListsStack.push({
        node: node,
        startPos: currentNodeStartPos
      });
      var _from = currentNodeStartPos;
      var _to = currentNodeStartPos + node.nodeSize;
      var depth = processedListsStack.length;
      decorations.push(Decoration.node(_from, _to, {
        'data-indent-level': "".concat(depth)
      }));
      if (node.type.name === 'orderedList') {
        var _node$attrs;
        // If a numbered list has item counters numbering >= 100, we'll need to add special
        // spacing to account for the extra digit chars
        var digitsSize = getItemCounterDigitsSize({
          itemsCount: node === null || node === void 0 ? void 0 : node.childCount,
          order: node === null || node === void 0 || (_node$attrs = node.attrs) === null || _node$attrs === void 0 ? void 0 : _node$attrs.order
        });
        if (digitsSize && digitsSize > 1) {
          decorations.push(Decoration.node(_from, _to, {
            style: getOrderedListInlineStyles(digitsSize, 'string')
          }));
        }
      }
    }
  });
  return decorations;
};
export var updateListDecorations = function updateListDecorations(decorationSet, tr) {
  // @ts-ignore - Workaround for help-center local consumption

  var nextDecorationSet = decorationSet.map(tr.mapping, tr.doc);

  // @ts-ignore - Workaround for help-center local consumption

  tr.mapping.maps.forEach(function (stepMap, index) {
    // @ts-ignore - Workaround for help-center local consumption

    stepMap.forEach(function (oldStart, oldEnd) {
      // @ts-ignore - Workaround for help-center local consumption

      var start = tr.mapping.slice(index).map(oldStart, -1);
      // @ts-ignore - Workaround for help-center local consumption

      var end = tr.mapping.slice(index).map(oldEnd);

      // Remove decorations in this range
      // @ts-ignore - Workaround for help-center local consumption

      var decorationsToRemove = nextDecorationSet.find(start, end);
      nextDecorationSet = nextDecorationSet.remove(decorationsToRemove);

      // Recompute decorations for this range
      // Expand the range by 1 on each side to catch adjacent list nodes
      var from = Math.max(0, start - 1);
      var to = Math.min(tr.doc.content.size, end + 1);
      var decorationsToAdd = computeListDecorations(tr.doc, from, to);
      nextDecorationSet = nextDecorationSet.add(tr.doc, decorationsToAdd);
    });
  });
  return nextDecorationSet;
};

// delete getDecorations during platform_editor_new_list_decorations_logic experiment clean up
export var getDecorations = function getDecorations(doc, state, featureFlags) {
  var decorations = [];

  // this stack keeps track of each (nested) list to calculate the indentation level
  var processedListsStack = [];

  // @ts-ignore - Workaround for help-center local consumption

  doc.nodesBetween(0, doc.content.size, function (node, currentNodeStartPos) {
    if (processedListsStack.length > 0) {
      var isOutsideLastList = true;
      while (isOutsideLastList && processedListsStack.length > 0) {
        var lastList = processedListsStack[processedListsStack.length - 1];
        var lastListEndPos = lastList.startPos + lastList.node.nodeSize;
        isOutsideLastList = currentNodeStartPos >= lastListEndPos;
        // once we finish iterating over each innermost list, pop the stack to
        // decrease the indent level attribute accordingly
        if (isOutsideLastList) {
          processedListsStack.pop();
        }
      }
    }
    if (isListNode(node)) {
      processedListsStack.push({
        node: node,
        startPos: currentNodeStartPos
      });
      var from = currentNodeStartPos;
      var to = currentNodeStartPos + node.nodeSize;
      var depth = processedListsStack.length;
      decorations.push(Decoration.node(from, to, {
        'data-indent-level': "".concat(depth)
      }));
      if (node.type.name === 'orderedList') {
        var _node$attrs2;
        // If a numbered list has item counters numbering >= 100, we'll need to add special
        // spacing to account for the extra digit chars
        var digitsSize = getItemCounterDigitsSize({
          itemsCount: node === null || node === void 0 ? void 0 : node.childCount,
          order: node === null || node === void 0 || (_node$attrs2 = node.attrs) === null || _node$attrs2 === void 0 ? void 0 : _node$attrs2.order
        });
        if (digitsSize && digitsSize > 1) {
          decorations.push(Decoration.node(from, to, {
            style: getOrderedListInlineStyles(digitsSize, 'string')
          }));
        }
      }
    }
  });
  return DecorationSet.empty.add(doc, decorations);
};
var getListState = function getListState(doc, selection) {
  var _doc$type$schema$node = doc.type.schema.nodes,
    bulletList = _doc$type$schema$node.bulletList,
    orderedList = _doc$type$schema$node.orderedList,
    taskList = _doc$type$schema$node.taskList;
  var listParent = findParentNodeOfType([bulletList, orderedList, taskList])(selection);
  var bulletListActive = !!listParent && listParent.node.type === bulletList;
  var orderedListActive = !!listParent && listParent.node.type === orderedList;
  var bulletListDisabled = !(bulletListActive || orderedListActive || isWrappingPossible(bulletList, selection));
  var orderedListDisabled = !(bulletListActive || orderedListActive || isWrappingPossible(orderedList, selection));
  return {
    bulletListActive: bulletListActive,
    bulletListDisabled: bulletListDisabled,
    orderedListActive: orderedListActive,
    orderedListDisabled: orderedListDisabled
  };
};
var handleDocChanged = function handleDocChanged(featureFlags) {
  return function (tr, pluginState, editorState) {
    var nextPluginState = handleSelectionChanged(tr, pluginState);
    var decorationSet = getDecorations(tr.doc, editorState, featureFlags);
    return _objectSpread(_objectSpread({}, nextPluginState), {}, {
      decorationSet: decorationSet
    });
  };
};
var handleSelectionChanged = function handleSelectionChanged(tr, pluginState) {
  var _getListState = getListState(tr.doc, tr.selection),
    bulletListActive = _getListState.bulletListActive,
    orderedListActive = _getListState.orderedListActive,
    bulletListDisabled = _getListState.bulletListDisabled,
    orderedListDisabled = _getListState.orderedListDisabled;
  if (bulletListActive !== pluginState.bulletListActive || orderedListActive !== pluginState.orderedListActive || bulletListDisabled !== pluginState.bulletListDisabled || orderedListDisabled !== pluginState.orderedListDisabled) {
    var nextPluginState = _objectSpread(_objectSpread({}, pluginState), {}, {
      bulletListActive: bulletListActive,
      orderedListActive: orderedListActive,
      bulletListDisabled: bulletListDisabled,
      orderedListDisabled: orderedListDisabled
    });
    return nextPluginState;
  }
  return pluginState;
};
var reducer = function reducer() {
  return function (state) {
    return state;
  };
};
var createInitialState = function createInitialState(featureFlags, api) {
  return function (state) {
    var isToolbarAIFCEnabled = Boolean(api === null || api === void 0 ? void 0 : api.toolbar);
    var getInitialDecorations = function getInitialDecorations() {
      var _insm$session, _insm$session2;
      (_insm$session = insm.session) === null || _insm$session === void 0 || _insm$session.startFeature('listDecorationsInit');
      var decorations = computeListDecorations(state.doc);
      (_insm$session2 = insm.session) === null || _insm$session2 === void 0 || _insm$session2.endFeature('listDecorationsInit');
      return decorations;
    };
    return _objectSpread(_objectSpread({}, isToolbarAIFCEnabled && expValEquals('platform_editor_toolbar_aifc_patch_3', 'isEnabled', true) ? getListState(state.doc, state.selection) : initialState), {}, {
      decorationSet: expValEquals('platform_editor_new_list_decorations_logic', 'isEnabled', true) ? DecorationSet.empty.add(state.doc, getInitialDecorations()) : getDecorations(state.doc, state, featureFlags)
    });
  };
};
export var createPlugin = function createPlugin(eventDispatch, featureFlags, api) {
  var _pluginFactory = pluginFactory(listPluginKey, reducer(), {
      onDocChanged: handleDocChanged(featureFlags),
      onSelectionChanged: handleSelectionChanged
    }),
    getPluginState = _pluginFactory.getPluginState,
    createPluginState = _pluginFactory.createPluginState;
  var pluginState = createPluginState(eventDispatch, createInitialState(featureFlags, api));
  var pluginStateInit = function pluginStateInit(_, state) {
    return createInitialState(featureFlags)(state);
  };
  var pluginStateApply = function pluginStateApply(tr, oldPluginState, _oldEditorState, _newEditorState) {
    var nextPluginState = oldPluginState;
    if (tr.docChanged) {
      var _insm$session3, _insm$session4;
      nextPluginState = handleSelectionChanged(tr, nextPluginState);
      (_insm$session3 = insm.session) === null || _insm$session3 === void 0 || _insm$session3.startFeature('listDecorationUpdate');
      var nextDecorationSet = updateListDecorations(nextPluginState.decorationSet, tr);
      (_insm$session4 = insm.session) === null || _insm$session4 === void 0 || _insm$session4.endFeature('listDecorationUpdate');
      nextPluginState = _objectSpread(_objectSpread({}, nextPluginState), {}, {
        decorationSet: nextDecorationSet
      });
    } else if (tr.selectionSet) {
      nextPluginState = handleSelectionChanged(tr, nextPluginState);
    }
    if (nextPluginState !== oldPluginState) {
      eventDispatch(listPluginKey, nextPluginState);
    }
    return nextPluginState;
  };
  return new SafePlugin({
    state: {
      init: expValEquals('platform_editor_new_list_decorations_logic', 'isEnabled', true) ? pluginStateInit : pluginState.init,
      apply: expValEquals('platform_editor_new_list_decorations_logic', 'isEnabled', true) ? pluginStateApply : pluginState.apply
    },
    key: listPluginKey,
    props: {
      // @ts-ignore - Workaround for help-center local consumption
      decorations: function decorations(state) {
        var _ref = expValEquals('platform_editor_new_list_decorations_logic', 'isEnabled', true) ? listPluginKey.getState(state) : getPluginState(state),
          decorationSet = _ref.decorationSet;
        return decorationSet;
      },
      // @ts-ignore - Workaround for help-center local consumption

      handleClick: function handleClick(view, pos, event) {
        var state = view.state;
        // Ignored via go/ees005
        // eslint-disable-next-line @atlaskit/editor/no-as-casting
        if (['LI', 'UL'].includes((event === null || event === void 0 ? void 0 : event.target).tagName)) {
          var _nodeAtPos$firstChild;
          var nodeAtPos = state.tr.doc.nodeAt(pos);
          var _view$state$schema$no = view.state.schema.nodes,
            listItem = _view$state$schema$no.listItem,
            codeBlock = _view$state$schema$no.codeBlock;
          if ((nodeAtPos === null || nodeAtPos === void 0 ? void 0 : nodeAtPos.type) === listItem && (nodeAtPos === null || nodeAtPos === void 0 || (_nodeAtPos$firstChild = nodeAtPos.firstChild) === null || _nodeAtPos$firstChild === void 0 ? void 0 : _nodeAtPos$firstChild.type) === codeBlock) {
            var _document;
            var bufferPx = 50;
            var isCodeBlockNextToListMarker = Boolean((_document = document) === null || _document === void 0 || (_document = _document.elementFromPoint(event.clientX + (listItemCounterPadding + bufferPx), event.clientY)) === null || _document === void 0 ? void 0 : _document.closest(".".concat(CodeBlockSharedCssClassName.CODEBLOCK_CONTAINER)));
            if (isCodeBlockNextToListMarker) {
              // +1 needed to put cursor inside li
              // otherwise gap cursor markup will be injected as immediate child of ul resulting in invalid html
              setGapCursorSelection(view, pos + 1, Side.LEFT);
              return true;
            }
          }
        }
        return false;
      }
    }
  });
};